/**
 * Normalize CMS / pasted video URLs so protocol-relative (`//youtube.com/...`)
 * and schemeless (`youtube.com/...`, `www.youtube.com/watch?v=…`) values still
 * parse. `new URL()` rejects both without a base, which broke play buttons when
 * editors saved a data-video-url without `https://` (issue #158).
 */
export const normalizeVideoUrl = (rawUrl) => {
	const trimmed = rawUrl?.trim();
	if (!trimmed) {
		return '';
	}

	// `https:///assets/videos/x.mp4` — a scheme with an EMPTY host, which is
	// what a root-relative path becomes once something prefixes it with
	// `https://`. The URL parser rejects an empty host on a special scheme, so
	// parseVideoUrl returned null and the play button did nothing (#248).
	// Strip it back to the path it was.
	const schemeWithEmptyHost = trimmed.match(/^[a-z][a-z0-9+.-]*:\/\/(\/[^/].*)$/i);
	if (schemeWithEmptyHost) {
		return schemeWithEmptyHost[1];
	}

	if (trimmed.startsWith('//')) {
		return `https:${trimmed}`;
	}

	// A same-origin path — an uploaded asset such as
	// `/assets/videos/x.mp4` (#248). Kept as a path; parseVideoUrl resolves it
	// against the current document.
	if (trimmed.startsWith('/')) {
		return trimmed;
	}

	// Already has a scheme (https:, http:, blob:, …).
	if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
		return trimmed;
	}

	return `https://${trimmed}`;
};

/**
 * Parse external video URLs (YouTube, Vimeo, direct file links).
 */
export const parseVideoUrl = (rawUrl) => {
	if (!rawUrl) {
		return null;
	}

	let url;

	try {
		// Base: a normalized value may be a same-origin path (see
		// normalizeVideoUrl). Absolute values ignore the base.
		url = new URL(
			normalizeVideoUrl(rawUrl),
			typeof window !== 'undefined' ? window.location.href : undefined,
		);
	} catch {
		return null;
	}

	const host = url.hostname.replace(/^www\./, '');
	const path = url.pathname;

	if (host === 'youtu.be') {
		const id = path.slice(1).split('/')[0];
		return id ? { type: 'youtube', id } : null;
	}

	if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
		if (path.startsWith('/embed/')) {
			const id = path.split('/')[2];
			return id ? { type: 'youtube', id } : null;
		}

		if (path.startsWith('/shorts/')) {
			const id = path.split('/')[2];
			return id ? { type: 'youtube', id } : null;
		}

		const id = url.searchParams.get('v');
		return id ? { type: 'youtube', id } : null;
	}

	if (host === 'vimeo.com') {
		const id = path.split('/').filter(Boolean)[0];
		return id && /^\d+$/.test(id) ? { type: 'vimeo', id } : null;
	}

	if (host === 'player.vimeo.com' && path.startsWith('/video/')) {
		const id = path.split('/')[2];
		return id ? { type: 'vimeo', id } : null;
	}

	if (/\.(mp4|webm|ogg)(\?|$)/i.test(path)) {
		return { type: 'file', src: url.href };
	}

	return { type: 'file', src: url.href };
};

// Without top-navigation / popups so YouTube logo & title links cannot leave the site.
const EMBED_IFRAME_SANDBOX =
	'allow-scripts allow-same-origin allow-presentation allow-forms';

const isCoarsePointerDevice = () =>
	typeof window !== 'undefined' &&
	window.matchMedia('(hover: none) and (pointer: coarse)').matches;

const configureEmbedIframe = (iframe) => {
	iframe.title = 'Video player';
	iframe.setAttribute('allowfullscreen', '');
	iframe.setAttribute(
		'allow',
		'autoplay; fullscreen; encrypted-media; picture-in-picture',
	);
	// Sandbox breaks touch controls in the YouTube mobile player.
	if (!isCoarsePointerDevice()) {
		iframe.setAttribute('sandbox', EMBED_IFRAME_SANDBOX);
	}
	iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
};

export const sendEmbedPlayerCommand = (iframe, parsed, command) => {
	if (!iframe?.contentWindow) {
		return;
	}

	if (parsed.type === 'youtube') {
		iframe.contentWindow.postMessage(
			JSON.stringify({ event: 'command', func: command, args: '' }),
			'*',
		);
		return;
	}

	if (parsed.type === 'vimeo') {
		const method =
			command === 'pauseVideo' ? 'pause' : command === 'playVideo' ? 'play' : command;
		iframe.contentWindow.postMessage(JSON.stringify({ method }), '*');
	}
};

export const buildEmbedSrc = (parsed) => {
	if (parsed.type === 'youtube') {
		const params = new URLSearchParams({
			autoplay: '1',
			rel: '0',
			modestbranding: '1',
			playsinline: '1',
			enablejsapi: '1',
			iv_load_policy: '3',
		});

		if (typeof window !== 'undefined' && window.location?.origin) {
			params.set('origin', window.location.origin);
		}

		return `https://www.youtube-nocookie.com/embed/${parsed.id}?${params}`;
	}

	if (parsed.type === 'vimeo') {
		const params = new URLSearchParams({
			autoplay: '1',
			title: '0',
			byline: '0',
			portrait: '0',
		});
		return `https://player.vimeo.com/video/${parsed.id}?${params}`;
	}

	return parsed.src;
};

export const mountVideoPlayer = (mediaEl, parsed) => {
	mediaEl.replaceChildren();

	if (parsed.type === 'file') {
		const video = document.createElement('video');
		video.className = 'cs-video-modal__video';
		video.src = parsed.src;
		video.controls = true;
		video.autoplay = true;
		video.playsInline = true;
		video.preload = 'none';
		video.setAttribute('preload', 'none');
		video.setAttribute('controlsList', 'nodownload');
		mediaEl.append(video);
		video.focus();
		return;
	}

	const iframe = document.createElement('iframe');
	iframe.className = 'cs-video-modal__iframe';
	iframe.src = buildEmbedSrc(parsed);
	configureEmbedIframe(iframe);
	mediaEl.append(iframe);
};
